/* eslint-disable @next/next/no-img-element */
import React from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import { NavigationOptions } from 'interfaces';
import AppRoutes from 'const/routes';
import { Icon, SolidDot, Typography } from 'components/common';
import { useRouter } from 'next/router';
import { CypressI } from 'interfaces/cypress';
import { PinnedCases } from './PinnedCases';
import UserMenu from './UserMenu';
import { CounterInbox } from './CounterInbox';
import { TriangleAlert } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import { useHandleError } from '@/hooks/useHandleError';
import { ActiveLine } from './ActiveLine';
import useHasActionsRequired from '@/hooks/useHasActionsRequired';

interface SidebarDesktopProps {
	itemOptions: NavigationOptions[];
	logoUrl?: string;
	collapsible?: boolean;
	classNameContainer?: string; // Container classes
	classNameItem?: string; // Typography classes
	classNameItemButton?: string; // Dropdown button classes
	showSidebar?: boolean;
	setShowSidebar?: (value: boolean) => void;
	role: 'client' | 'lawyer' | undefined;
}

/**
 * This component is meant to handle a side navbar
 *
 * @param itemOptions The options to be rendered in the navbar
 * @param logoUrl image to show in the sidebar
 * @param collapsible to make the sidebar able to collapse
 * @param showSidebar to show sidebar when it's mobile screen
 * @returns
 */
export const SidebarDesktop: React.FC<SidebarDesktopProps & CypressI> = ({
	dataCY,
	itemOptions,
	logoUrl,
	classNameContainer = 'bg-white',
	classNameItem = 'text-black',
	// classNameItemButton = '',
	showSidebar,
	role,
	setShowSidebar,
}) => {
	const session = useSession();
	const router = useRouter();
	const { hasCasesErrors, hasMarketingErrors } = useHandleError();
	const { hasActionsRequired } = useHasActionsRequired();

	const getIsCurrentOption = (item: NavigationOptions): boolean => {
		const path = router.pathname;
		const hasFeedbackParam = router.asPath.includes('feedback=true');

		if (hasFeedbackParam) return false;

		if (item.name === 'dashboard') {
			return path === item.href;
		}

		if (path.includes('client-profile')) {
			return (
				!!item.href?.includes('client-directory') &&
				!item.href?.includes('cleanup-wizard')
			);
		}

		return path.startsWith(item.href || '');
	};

	return (
		<div
			className={clsx(
				'-translate-x-full transition-transform border-r border-opacity-primary', // general
				'fixed w-full overflow-y-auto z-20 rounded-b-xl shadow-shadow2', // mobile
				'lg:relative lg:flex lg:flex-shrink-0 lg:z-0 lg:shadow-none lg:rounded-none', // desktop
				'lg:w-[105px] lg:translate-x-0', // desktop
				{ '-translate-x-full': !showSidebar },
				{ 'translate-x-0': showSidebar },
				classNameContainer
			)}
		>
			<nav
				data-cy={dataCY}
				className={clsx(
					'h-auto lg:h-full overflow-y-auto scroll-custom',
					'flex flex-col flex-1',
					'md:py-6 mt-20 lg:mt-0 gap-y-8 lg:gap-y-0',
					classNameContainer
				)}
			>
				{logoUrl && (
					<Link
						href={
							role === 'client'
								? AppRoutes.CLIENTAPP_DASHBOARD
								: role === 'lawyer'
								? AppRoutes.LAWYERAPP_DASHBOARD
								: '#'
						}
						className="h-auto px-6 mb-8 hidden lg:block"
					>
						<Image
							src={logoUrl}
							alt="Logo"
							className="size-10 mx-auto"
							height={40}
							width={40}
							priority
							quality={50}
						/>
					</Link>
				)}
				<div className="flex flex-col gap-8 lg:gap-2">
					{itemOptions?.map((item) => {
						const isActive = getIsCurrentOption(item);

						return (
							<div
								key={`sideNavbar-item-${item.name}`}
								className={clsx(
									'flex flex-wrap gap-x-2 w-full lg:justify-center items-center px-4 lg:px-2 relative',
									{ 'flex lg:hidden': item.isMobile }
								)}
							>
								{isActive && <ActiveLine />}
								<div
									className={clsx(
										'flex flex-col lg:justify-center items-center gap-2 px-2 py-[5px] relative'
									)}
								>
									<Link
										data-cy={`${dataCY}-option-${item.name}`}
										href={item.href ?? '#'}
										className="flex flex-row lg:flex-col justify-start lg:justify-center gap-2 items-center w-full relative"
										scroll={true}
										onClick={(e) => {
											item.onClick?.(e);
											setTimeout(() => {
												setShowSidebar?.(false);
											}, 500);
										}}
									>
										{/* Icon */}
										{(item.name === 'cases' && hasCasesErrors) ||
										(item.name === 'marketing' && hasMarketingErrors) ? (
											<TriangleAlert
												className={clsx(
													'size-5',
													getIsCurrentOption(item)
														? 'text-primary'
														: 'text-main-red'
												)}
											/>
										) : item.icon ? (
											<Icon
												width={20}
												height={20}
												src={item.icon}
												className={clsx(
													isActive ? 'text-primary' : 'text-opacity-black'
												)}
											/>
										) : null}

										<Typography
											type="semibold-12"
											className={clsx(
												'text-center',
												getIsCurrentOption(item)
													? 'text-primary'
													: 'text-opacity-black',
												classNameItem
											)}
										>
											{item.label}
										</Typography>
									</Link>
									{/* Inbox counter */}
									{item?.href?.includes('inbox') && <CounterInbox />}
									{hasActionsRequired &&
										(item?.href?.includes('dashboard') ||
											item.href?.includes('/cases')) && (
											<SolidDot
												size={'10px'}
												className={clsx(
													'absolute left-[22px] top-1 lg:left-auto',
													{
														'lg:top-0 lg:right-7':
															item?.href?.includes('dashboard'),
														'lg:top-1 lg:right-3':
															item?.href?.includes('/lawyerapp/cases'),
														'lg:top-1 lg:right-[23px]':
															item?.href?.includes('/clientapp/cases'),
													}
												)}
											/>
										)}
								</div>
							</div>
						);
					})}
				</div>

				{/* Pinned Cases */}
				{role && (
					<div className="mt-auto">
						<PinnedCases role={role} />
					</div>
				)}

				{role && (
					<div className="mt-auto">
						<UserMenu
							role={role}
							session={session}
							setShowSidebar={setShowSidebar}
						/>
					</div>
				)}
			</nav>
		</div>
	);
};
